import { useMemo } from 'react';
import { useCart, priceCart, stepFor, type CartLine } from '../store/cart';
import type { RoundOffMode } from '@sp/core';
import { amount, daysUntil, maskPhone, percentFromBps, quantity, rupees, rupeesCompact, unitLabel } from '../lib/format';
import { expiryNote, lineMrpSavingsPaise, lastVisitLabel, mrpSavingsPaise } from '../lib/till';
import { firstName } from '../lib/display';
import { Icon } from './icons';
import { cx, Kbd } from './ui';
import { tr, translate, useLanguage, useT } from '../i18n';

/**
 * The bill.
 *
 * Prices come from the shared pricing engine rather than being recalculated here, so the
 * amount on this panel is the amount that will be printed on the invoice. Around that number
 * the panel answers what the cashier and the customer ask at the counter: who is this for (a
 * returning customer's visits and spend), what did I just scan (the highlighted line), is
 * anything short-dated (an expiry tag), and how much is the customer saving (MRP savings and
 * discounts, called out in green). The total is in display type: it is the number quoted.
 */

interface Keys {
  customer?: string;
  discount?: string;
  hold?: string;
  newSale?: string;
  payment?: string;
}

interface Props {
  interState: boolean;
  roundOffMode: RoundOffMode;
  disabled?: boolean;
  onEditLine?: (key: string) => void;
  onPay: () => void;
  canPay: boolean;
  onCustomer?: () => void;
  onDetachCustomer?: () => void;
  onDiscount?: () => void;
  onHold?: () => void;
  onNewSale?: () => void;
  /** Phone only: the empty bill's way back to the scan bar and the products. */
  onAddItems?: () => void;
  keys?: Keys;
  /** Rendered in the empty state: quick-start hints. */
  hints?: React.ReactNode;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

export function CartPanel({
  interState,
  roundOffMode,
  disabled = false,
  onEditLine,
  onPay,
  canPay,
  onCustomer,
  onDetachCustomer,
  onDiscount,
  onHold,
  onNewSale,
  onAddItems,
  keys = {},
  hints,
}: Props) {
  const lines = useCart((state) => state.lines);
  const billDiscount = useCart((state) => state.billDiscount);
  const customer = useCart((state) => state.customer);
  const customerName = useCart((state) => state.customerName);
  const customerPhone = useCart((state) => state.customerPhone);
  const lastAddedKey = useCart((state) => state.lastAdded?.key ?? null);
  const taxExempt = useCart((state) => state.taxExempt);
  const t = useT();

  const { priced, totals, taxRemovedPaise, taxReliefPaise } = useMemo(
    () => priceCart(lines, billDiscount, interState, roundOffMode, taxExempt),
    [lines, billDiscount, interState, roundOffMode, taxExempt],
  );
  /** Discounts the cashier gave; the No Tax relief has its own row. */
  const givenDiscountPaise = totals.totalDiscountPaise - taxReliefPaise;

  const gstPaise = totals.tax.igstPaise > 0 ? totals.tax.igstPaise : totals.tax.cgstPaise + totals.tax.sgstPaise;
  const belowMrpPaise = mrpSavingsPaise(lines);
  const savedPaise = belowMrpPaise + givenDiscountPaise;
  const empty = lines.length === 0;
  const payKey = keys.payment ?? 'F10';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 px-4 short:h-11">
        <h2 className="text-base font-bold text-slate-900">{t('cart.title')}</h2>
        {taxExempt ? (
          <span className="badge badge-warn" data-testid="cart-no-tax">
            {t('cart.noTax')}
          </span>
        ) : null}
        {empty ? null : (
          <span className="badge" data-testid="cart-line-count">
            {t('cart.lines', { count: lines.length })}
          </span>
        )}
        <span className="flex-1" />
        {onNewSale ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onNewSale} data-testid="pos-new-sale" disabled={disabled && empty}>
            {t('cart.newBill')}
            {keys.newSale ? <Kbd className="max-sm:hidden">{keys.newSale}</Kbd> : null}
          </button>
        ) : null}
      </header>

      {customerName ? (
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-2.5 short:py-1.5" style={{ background: 'var(--ui-primary-soft)' }} data-testid="customer-chip">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold short:h-8 short:w-8 short:text-xs"
            style={{ background: 'var(--ui-primary)', color: 'var(--ui-primary-ink)' }}
          >
            {initialsOf(customerName)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex min-w-0 items-center gap-2">
              <button type="button" className="truncate text-left text-[0.95rem] font-bold text-slate-900" disabled={disabled} onClick={onCustomer}>{customerName}</button>
              {customer?.visitCount !== null && customer?.visitCount !== undefined ? (
                customer.visitCount >= 5 ? (
                  <span className="badge shrink-0 bg-white" style={{ color: 'var(--ui-primary)' }}>
                    {t('cart.regular')}
                  </span>
                ) : customer.visitCount === 0 ? (
                  <span className="badge badge-success shrink-0">{t('cart.firstVisit')}</span>
                ) : null
              ) : null}
            </p>
            <p className="tabular truncate text-xs text-slate-600">
              {customerPhone ? maskPhone(customerPhone) : ''}
              {customer?.visitCount ? ` · ${t('cart.visits', { count: customer.visitCount })}` : ''}
              {customer?.totalSpendPaise ? ` · ${t('cart.spent', { amount: rupeesCompact(customer.totalSpendPaise) })}` : ''}
              {customer?.lastVisitAt ? ` · ${t('cart.lastHere', { when: lastVisitLabel(customer.lastVisitAt) ?? '' })}` : ''}
            </p>
          </div>
          {onDetachCustomer ? (
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-black/10"
              aria-label={t('cart.detach', { name: customerName })}
              disabled={disabled}
              onClick={onDetachCustomer}
            >
              <Icon name="close" size={16} />
            </button>
          ) : null}
        </div>
      ) : onCustomer ? (
        <div className="shrink-0 border-b border-slate-200 px-4 py-2.5 short:py-1.5">
          <button
            type="button"
            className="btn btn-touch btn-sm w-full justify-start border-dashed bg-transparent text-slate-600 shadow-none short:min-h-9"
            disabled={disabled}
            onClick={onCustomer}
            data-testid="pos-customer"
          >
            <Icon name="user" size={17} className="shrink-0" />
            <span className="max-w-full shrink-0 truncate">{t('cart.addCustomer')}</span>
            <span className="hidden min-w-0 truncate font-normal text-slate-400 2xl:inline">· {t('cart.customerHint')}</span>
            {keys.customer ? <Kbd className="ml-auto max-sm:hidden">{keys.customer}</Kbd> : null}
          </button>
        </div>
      ) : null}

      {empty ? (
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-6 text-center" data-testid="cart-empty">
          <div className="max-w-xs">
            <div
              className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ background: 'var(--ui-primary-soft)', color: 'var(--ui-primary)' }}
              aria-hidden
            >
              <Icon name="cart" size={30} />
            </div>
            <p className="text-base font-semibold text-slate-800">{t('cart.emptyTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('cart.emptyHint')}</p>
            {onAddItems ? (
              <button type="button" className="btn btn-primary mt-4 w-full md:hidden" onClick={onAddItems} data-testid="cart-add-items">
                <Icon name="plus" size={18} strokeWidth={2.4} />
                {t('cart.addItems')}
              </button>
            ) : null}
            {hints ? <div className="mt-4 text-left">{hints}</div> : null}
          </div>
        </div>
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto" aria-label={t('cart.itemsLabel')}>
          {lines.map((line, index) => (
            <BillLine
              key={line.key}
              line={line}
              totalPaise={priced[index]?.totalPaise ?? 0}
              discountPaise={priced[index] ? priced[index].itemDiscountPaise + priced[index].billDiscountPaise - (priced[index].taxReliefPaise ?? 0) : 0}
              isNew={line.key === lastAddedKey}
              disabled={disabled}
              onEdit={onEditLine}
            />
          ))}
        </ul>
      )}

      <div className="shrink-0 space-y-2.5 border-t border-slate-200 bg-slate-50 px-4 py-3 short:space-y-2 short:py-2">
        {empty ? null : (
          <dl className="space-y-1 text-sm short:space-y-0 short:text-[13px]" aria-label={t('cart.totalsLabel')}>
            <Row
              label={`${t('cart.subtotal')} · ${t('cart.lines', { count: totals.lineCount })}, ${quantity(totals.totalQtyMilli)} ${t('cart.units')}`}
              // With No Tax the subtotal is shown before the tax came off, so the rows below add up.
              value={`₹${amount(totals.subTotalPaise + taxRemovedPaise)}`}
            />
            {givenDiscountPaise > 0 ? <Row label={t('cart.discountGiven')} value={`−₹${amount(givenDiscountPaise)}`} tone="good" /> : null}
            {taxExempt ? <Row label={t('cart.taxRemoved')} value={`−₹${amount(taxRemovedPaise)}`} tone="good" testId="cart-tax-removed" /> : null}
            <Row label={interState ? t('cart.igstIncluded') : t('cart.gstIncluded')} value={`₹${amount(gstPaise)}`} />
            {totals.roundOffPaise !== 0 ? (
              <Row label={t('cart.roundOff')} value={`${totals.roundOffPaise > 0 ? '+' : '−'}₹${amount(Math.abs(totals.roundOffPaise))}`} />
            ) : null}
          </dl>
        )}

        {savedPaise > 0 ? (
          <p className="savings-ribbon" data-testid="cart-savings">
            <Icon name="tag" size={16} strokeWidth={2} />
            <span className="min-w-0 flex-1">
              {t('cart.saves', { name: firstName(customerName) ?? t('cart.theCustomer'), amount: amount(savedPaise) })}
              <span className="font-normal">
                {belowMrpPaise > 0 && givenDiscountPaise > 0
                  ? t('cart.savesBoth', { mrp: amount(belowMrpPaise), discount: amount(givenDiscountPaise) })
                  : belowMrpPaise > 0
                    ? t('cart.savesMrp')
                    : t('cart.savesDiscount')}
              </span>
            </span>
          </p>
        ) : null}

        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-500">{t('cart.toPay')}</p>
            <p className="sr-only">{rupees(totals.grandTotalPaise)}</p>
          </div>
          <p className="tabular text-[2.6rem] leading-none font-extrabold tracking-tight text-slate-900 short:text-[2rem]">
            <span aria-hidden className="mr-0.5 text-2xl font-bold text-slate-400">
              ₹
            </span>
            <span data-testid="cart-total">{amount(totals.grandTotalPaise)}</span>
          </p>
        </div>

        {/* On a short laptop screen Discount and Hold sit beside Pay, one row instead of two. */}
        <div className="flex flex-col gap-2 short:flex-row short:flex-wrap">
          {empty || (!onDiscount && !onHold) ? null : (
            <div className="grid grid-cols-2 gap-2 short:flex short:grow">
              {onDiscount ? (
                <button
                  type="button"
                  className="btn btn-touch whitespace-nowrap short:min-h-12 short:flex-1 short:px-3"
                  aria-pressed={billDiscount.type !== 'none'}
                  disabled={disabled}
                  onClick={onDiscount}
                  data-testid="pos-discount"
                >
                  {billDiscount.type !== 'none' ? t('cart.discountOn') : t('cart.discount')}
                  {keys.discount ? <Kbd className="max-sm:hidden short:hidden">{keys.discount}</Kbd> : null}
                </button>
              ) : null}
              {onHold ? (
                <button type="button" className="btn btn-touch whitespace-nowrap short:min-h-12 short:flex-1 short:px-3" disabled={disabled} onClick={onHold} data-testid="pos-hold-bill">
                  <Icon name="pause" size={16} />
                  {t('cart.hold')}
                  {keys.hold ? <Kbd className="max-sm:hidden short:hidden">{keys.hold}</Kbd> : null}
                </button>
              ) : null}
            </div>
          )}

          <button
            type="button"
            className="btn btn-success min-h-14 w-full rounded-xl text-lg whitespace-nowrap short:min-h-12 short:w-auto short:grow-[2]"
            data-testid="pos-pay"
            disabled={!canPay || disabled || empty}
            onClick={onPay}
          >
            {t('cart.pay')} ₹{amount(totals.grandTotalPaise)}
            <Kbd>{payKey}</Kbd>
          </button>
        </div>
      </div>
    </div>
  );
}

function BillLine({
  line,
  totalPaise,
  discountPaise,
  isNew,
  disabled,
  onEdit,
}: {
  line: CartLine;
  totalPaise: number;
  discountPaise: number;
  isNew: boolean;
  disabled: boolean;
  onEdit?: (key: string) => void;
}) {
  const language = useLanguage((state) => state.language);
  const t = useT();
  const setQty = useCart((state) => state.setQty);
  const increment = useCart((state) => state.increment);
  const removeLine = useCart((state) => state.removeLine);
  const reject = useCart((state) => state.reject);

  const step = stepFor(line);
  const unit = unitLabel(line.unit);
  // Selling above MRP is a pricing mistake, not a till mistake, but the cashier should see it before the customer does.
  const aboveMrp = line.mrpPaise > 0 && line.ratePaise > line.mrpPaise;
  const saves = lineMrpSavingsPaise(line);
  const expiry = expiryNote(daysUntil(line.expiryDate));

  return (
    <li className="bill-line px-4 py-2.5 short:py-1.5" data-testid="cart-line" data-new={isNew ? 'true' : undefined}>
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => onEdit?.(line.key)}
          title={onEdit ? t('cart.editLine') : undefined}
        >
          <span className="block truncate text-[0.95rem] font-semibold text-slate-900">
            {line.productName}
            {line.localName ? <span className="font-normal text-slate-500"> · {line.localName}</span> : null}
          </span>
        </button>
        <div className="shrink-0 text-right">
          <p className="tabular text-base leading-tight font-bold text-slate-900">₹{amount(totalPaise)}</p>
          {discountPaise > 0 ? (
            <p className="tabular text-xs" style={{ color: 'var(--ui-success)' }}>
              −{amount(discountPaise)}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-2 short:mt-0.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="tabular text-xs whitespace-nowrap text-slate-500">
            ₹{amount(line.ratePaise)}
            {line.allowDecimalQty ? `/${unit}` : ''} × {quantity(line.qtyMilli)}
            {line.allowDecimalQty ? ` ${unit}` : ''}
          </span>
          {expiry ? <span className={cx('badge', expiry.tone === 'danger' ? 'badge-danger' : 'badge-warn')}>{expiry.text}</span> : null}
          {aboveMrp ? <span className="badge badge-warn">{t('cart.aboveMrp', { mrp: amount(line.mrpPaise) })}</span> : null}
          {saves > 0 ? (
            <span className="badge badge-success">
              {t('cart.mrpSaves', { mrp: amount(line.mrpPaise).replace(/\.00$/, ''), saves: amount(saves).replace(/\.00$/, '') })}
            </span>
          ) : null}
          {line.discountKind !== 'none' ? (
            <span className="badge badge-success">
              {t('cart.off', { value: line.discountKind === 'percent' ? `${percentFromBps(line.discountValue)}%` : `₹${amount(line.discountValue)}` })}
            </span>
          ) : null}
          {line.allowDecimalQty ? <span className="badge">{t('cart.weighed')}</span> : null}
          {isNew ? <span className="badge badge-solid">{translate(language, 'cart.justAdded')}</span> : null}
        </div>

        <div className="stepper-sm h-9 shrink-0 short:h-8">
          <button type="button" disabled={disabled || line.qtyMilli <= step} aria-label={t('cart.decrease', { name: line.productName })} onClick={() => increment(line.key, -step)}>
            −
          </button>
          <input
            className="tabular text-sm text-slate-900"
            value={quantity(line.qtyMilli)}
            disabled={disabled}
            inputMode="decimal"
            aria-label={t('cart.quantity', { name: line.productName })}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => {
              // Clearing the field to retype must not destroy the line: ignore the
              // empty draft here and let blur restore it. Only a committed value changes qty.
              if (event.target.value === '') return;
              const parsed = Number.parseFloat(event.target.value);
              if (Number.isFinite(parsed) && parsed > 0) setQty(line.key, Math.round(parsed * 1000));
            }}
            onBlur={(event) => {
              if (event.target.value.trim() === '') return;
              const parsed = Number.parseFloat(event.target.value);
              if (!Number.isFinite(parsed) || parsed <= 0) {
                reject(tr('pos.needsQty', { name: line.productName }));
                setQty(line.key, stepFor(line));
              }
            }}
          />
          <button type="button" disabled={disabled} aria-label={t('cart.increase', { name: line.productName })} onClick={() => increment(line.key, step)}>
            +
          </button>
        </div>

        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 short:h-8 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
          disabled={disabled}
          aria-label={t('cart.remove', { name: line.productName })}
          title={t('cart.removeTitle')}
          onClick={() => removeLine(line.key)}
        >
          <Icon name="trash" size={17} />
        </button>
      </div>
    </li>
  );
}

function Row({ label, value, tone, testId }: { label: string; value: string; tone?: 'good'; testId?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testId}>
      <dt className="text-slate-600" style={tone === 'good' ? { color: 'var(--ui-success)' } : undefined}>
        {label}
      </dt>
      <dd className="tabular font-medium text-slate-800" style={tone === 'good' ? { color: 'var(--ui-success)' } : undefined}>
        {value}
      </dd>
    </div>
  );
}
